import { getIdTokenResult, type User } from "firebase/auth";
import { useCallback, useEffect, useState } from "react";

import { gameApi, friendlyError } from "../lib/api";
import { auth } from "../lib/firebase";
import { hasPaidPremium } from "../lib/premium";
import type { PremiumStatus } from "../lib/types";

type State = { uid: string; status: PremiumStatus | null; loading: boolean; error: string };

export function usePremiumStatus(user: User | null | undefined) {
  const uid = user && !user.isAnonymous ? user.uid : null;
  const [state, setState] = useState<State | null>(null);

  const refresh = useCallback(async () => {
    if (!uid || !user) return;
    setState((current) => ({ uid, status: current?.uid === uid ? current.status : null, loading: true, error: "" }));
    try {
      const status = await gameApi.getPremiumStatus({ force: true });
      if (auth.currentUser?.uid !== uid) return;
      // Room connections read the signed claim. Refresh it after a webhook has
      // confirmed payment so the new border appears without another sign-in.
      let tokenError = "";
      if (status.active && status.source) {
        try {
          const token = await getIdTokenResult(user);
          const claimedUntil = token.claims.premiumUntil;
          if (!hasPaidPremium(claimedUntil) || Number(claimedUntil) < (status.accessUntil ?? 0) / 1000 - 1) {
            await user.getIdToken(true);
          }
        } catch (error) {
          tokenError = `Premium is active, but your room badge may need a sign-in refresh: ${friendlyError(error)}`;
        }
      }
      setState({ uid, status, loading: false, error: tokenError });
    } catch (error) {
      if (auth.currentUser?.uid === uid) setState((current) => ({ uid, status: current?.uid === uid ? current.status : null, loading: false, error: friendlyError(error) }));
    }
  }, [uid, user]);

  useEffect(() => {
    if (!uid) { setState(null); return; }
    void refresh();
    const onFocus = () => { if (document.visibilityState === "visible") void refresh(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [uid, refresh]);

  const current = state?.uid === uid ? state : null;
  return { status: current?.status ?? null, loading: Boolean(uid && (!current || current.loading)), error: current?.error ?? "", refresh };
}
